import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Spinner } from 'react-bootstrap';
import { doc, onSnapshot } from 'firebase/firestore';
import { FaArrowRight, FaCheckCircle, FaClock, FaRegCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { NIVELES_EVALUACION, crearClaveProgreso } from '../../config/evaluacionesCatalogo';
import { db } from '../../firebase/firebase';
import {
  cargarProgresoHistorico,
  combinarProgresoEvaluaciones,
} from '../../utils/progresoEvaluacionesHistorico';
import './ProgresoEvaluacionesAlumno.scss';

const ESTADO_CONFIG = {
  completado: { texto: 'Completado', icono: <FaCheckCircle />, clase: 'completado' },
  'en-curso': { texto: 'En curso', icono: <FaClock />, clase: 'en-curso' },
  pendiente: { texto: 'Pendiente', icono: <FaRegCircle />, clase: 'pendiente' },
};

const RUTAS_NIVEL = {
  basico: '/evaluacion-basica',
  intermedio: '/evaluacion-intermedia',
  avanzado: '/evaluacion-avanzada',
};

const formatearFecha = (valor) => {
  const fecha = valor?.toDate?.() || (valor ? new Date(valor) : null);
  if (!fecha || Number.isNaN(fecha.getTime())) return 'Sin actividad';
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(fecha);
};

const ProgresoEvaluacionesAlumno = ({ alumnoId }) => {
  const navigate = useNavigate();
  const [alumno, setAlumno] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progresoHistorico, setProgresoHistorico] = useState({});

  useEffect(() => {
    if (!alumnoId || !db) {
      setError('No fue posible identificar al alumno.');
      setLoading(false);
      return undefined;
    }

    return onSnapshot(
      doc(db, 'users', alumnoId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setError('No se encontró la información del alumno.');
          setAlumno(null);
        } else {
          setAlumno({ id: snapshot.id, ...snapshot.data() });
          setError('');
        }
        setLoading(false);
      },
      (snapshotError) => {
        console.error('Error consultando progreso del alumno:', snapshotError);
        setError('No se pudo cargar el progreso de evaluaciones.');
        setLoading(false);
      }
    );
  }, [alumnoId]);

  useEffect(() => {
    let activo = true;
    if (!alumnoId) return undefined;

    cargarProgresoHistorico(alumnoId)
      .then((resultado) => {
        if (activo) setProgresoHistorico(resultado);
      })
      .catch((historicoError) => {
        console.error('Error consultando respuestas históricas:', historicoError);
        if (activo) setError('No se pudieron revisar las respuestas anteriores del alumno.');
      });

    return () => { activo = false; };
  }, [alumnoId]);

  const progresoEfectivo = useMemo(() => combinarProgresoEvaluaciones(
    alumno?.progresoEvaluaciones || {},
    progresoHistorico
  ), [alumno?.progresoEvaluaciones, progresoHistorico]);

  const resumen = useMemo(() => {
    const registros = Object.values(progresoEfectivo);
    return {
      completados: registros.filter((item) => item.estado === 'completado').length,
      enCurso: registros.filter((item) => item.estado === 'en-curso').length,
      ultimo: registros.reduce((masReciente, item) => {
        const actual = item.actualizadoEn?.toMillis?.() || 0;
        const anterior = masReciente?.actualizadoEn?.toMillis?.() || 0;
        return actual > anterior ? item : masReciente;
      }, null),
    };
  }, [progresoEfectivo]);

  if (loading) {
    return <div className="progreso-evaluaciones__state"><Spinner animation="border" /><span>Cargando progreso...</span></div>;
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  const progresoGuardado = progresoEfectivo;

  const irAlEjercicio = (nivel, ejercicio) => {
    const ruta = RUTAS_NIVEL[nivel];
    if (!ruta) return;
    const estado = ejercicio.registro?.estado || 'pendiente';
    const parametros = new URLSearchParams({
      ejercicio: ejercicio.clave,
      modo: 'consulta',
      alumno: alumnoId,
      estado,
    });
    navigate(`${ruta}?${parametros.toString()}`);
  };

  return (
    <div className="progreso-evaluaciones">
      <header className="progreso-evaluaciones__intro">
        <div>
          <span className="progreso-evaluaciones__eyebrow">Seguimiento académico</span>
          <h2>Progreso en ejercicios</h2>
          <p>Los ejercicios completados permanecen disponibles para repaso y no pierden su avance.</p>
        </div>
        <div className="progreso-evaluaciones__summary" aria-label="Resumen de progreso">
          <div><strong>{resumen.completados}</strong><span>Completados</span></div>
          <div><strong>{resumen.enCurso}</strong><span>En curso</span></div>
          <div><strong>{formatearFecha(resumen.ultimo?.actualizadoEn)}</strong><span>Última actividad</span></div>
        </div>
      </header>

      {Object.entries(NIVELES_EVALUACION).map(([nivelKey, nivel]) => (
        <section className="progreso-nivel" key={nivelKey}>
          <h3>{nivel.nombre}</h3>
          <div className="progreso-nivel__modules">
            {Object.entries(nivel.modulos).map(([modulo, ejercicios]) => {
              const registros = ejercicios.map((ejercicio) => {
                const clave = crearClaveProgreso(nivelKey, modulo, ejercicio.id);
                return { ...ejercicio, clave, registro: progresoGuardado[clave] };
              });
              const completados = registros.filter((item) => item.registro?.estado === 'completado').length;
              const porcentaje = Math.round((completados / ejercicios.length) * 100);

              return (
                <article className="progreso-modulo" key={modulo}>
                  <div className="progreso-modulo__header">
                    <div>
                      <h4>{modulo.replace('Modulo', 'Módulo ')}</h4>
                      <span>{completados} de {ejercicios.length} ejercicios</span>
                    </div>
                    <Badge className={porcentaje === 100 ? 'is-complete' : ''}>
                      {porcentaje === 100 && <FaCheckCircle />} {porcentaje}%
                    </Badge>
                  </div>
                  <div className="progreso-modulo__track" aria-hidden="true">
                    <span style={{ width: `${porcentaje}%` }} />
                  </div>
                  <ul>
                    {registros.map((ejercicio) => {
                      const estado = ejercicio.registro?.estado || 'pendiente';
                      const config = ESTADO_CONFIG[estado] || ESTADO_CONFIG.pendiente;
                      return (
                        <li key={ejercicio.clave}>
                          <div className="progreso-modulo__exercise">
                            <span className={`progreso-estado progreso-estado--${config.clase}`}>
                              {config.icono} {config.texto}
                            </span>
                            <strong>{ejercicio.nombre}</strong>
                          </div>
                          <div className="progreso-modulo__access">
                            <small>{formatearFecha(ejercicio.registro?.actualizadoEn)}</small>
                            <button
                              type="button"
                              onClick={() => irAlEjercicio(nivelKey, ejercicio)}
                              aria-label={`Ir directamente a ${ejercicio.nombre}`}
                            >
                              Ir al ejercicio <FaArrowRight aria-hidden="true" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ProgresoEvaluacionesAlumno;
